import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  ArrowLeft, Flag, MessageCircle, User, Clock, CheckCircle, 
  XCircle, ArrowUpRight, AlertTriangle, Ban
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const AdminReportDetail = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null);
  const [resolution, setResolution] = useState('');
  const [selectedAction, setSelectedAction] = useState('no_action');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchReportDetail();
  }, [reportId]);

  const fetchReportDetail = async () => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load report');
      navigate('/admin/reports');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!resolution.trim()) {
      toast.error('Please provide resolution notes');
      return;
    }

    setProcessing(true);
    try {
      const payload = {
        report_id: reportId,
        action: selectedAction,
        resolution_notes: resolution
      };

      if (selectedAction === 'user_suspended' || selectedAction === 'user_banned') {
        payload.user_action = {
          user_id: data.report.reported_user_id,
          reason: `Report #${reportId.slice(0, 8)}: ${resolution}`
        };
      }

      await axios.post(`${API}/reports/${reportId}/resolve`, payload);
      toast.success('Report resolved');
      fetchReportDetail();
      setActionModal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve report');
    } finally {
      setProcessing(false);
    }
  };

  const handleEscalate = async () => {
    if (!resolution.trim()) {
      toast.error('Please provide escalation notes');
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/reports/${reportId}/escalate?notes=${encodeURIComponent(resolution)}`);
      toast.success('Report escalated');
      fetchReportDetail();
      setActionModal(null);
    } catch (error) {
      toast.error('Failed to escalate report');
    } finally {
      setProcessing(false);
    }
  };

  const handleDismiss = async () => {
    if (!resolution.trim()) {
      toast.error('Please provide dismissal reason');
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/reports/${reportId}/dismiss?reason=${encodeURIComponent(resolution)}`);
      toast.success('Report dismissed');
      fetchReportDetail();
      setActionModal(null);
    } catch (error) {
      toast.error('Failed to dismiss report');
    } finally {
      setProcessing(false);
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('data:') || photo.startsWith('http')) return photo;
    return `data:image/jpeg;base64,${photo}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/10 text-yellow-500',
      in_progress: 'bg-blue-500/10 text-blue-500',
      resolved: 'bg-green-500/10 text-green-500',
      escalated: 'bg-red-500/10 text-red-500',
      dismissed: 'bg-gray-500/10 text-gray-500'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || ''}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { report, reporter, reported_user, messages, previous_reports } = data;
  const isResolved = ['resolved', 'dismissed'].includes(report.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/reports')}
            className="text-[#A1A1AA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Reports
          </Button>
        </div>
        {!isResolved && (
          <div className="flex gap-2">
            <Button onClick={() => setActionModal('dismiss')} variant="outline" className="border-gray-500 text-gray-400">
              <XCircle className="w-4 h-4 mr-2" />
              Dismiss
            </Button>
            <Button onClick={() => setActionModal('escalate')} variant="outline" className="border-orange-500 text-orange-500">
              <ArrowUpRight className="w-4 h-4 mr-2" />
              Escalate
            </Button>
            <Button onClick={() => setActionModal('resolve')} className="bg-[#FF2E63] hover:bg-[#E01E50]">
              <CheckCircle className="w-4 h-4 mr-2" />
              Resolve
            </Button>
          </div>
        )}
      </div>

      {/* Report Info */}
      <div className="bg-[#111] border border-white/10 rounded-xl p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Flag className="w-5 h-5 text-red-500" />
              <h2 className="text-xl font-bold text-white">Report #{report.id.slice(0, 8)}</h2>
              {getStatusBadge(report.status)}
            </div>
            <p className="text-[#A1A1AA]">
              Submitted on {new Date(report.timestamp).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="p-4 bg-white/5 rounded-lg">
          <Label className="text-[#A1A1AA] text-sm">Reason</Label>
          <p className="text-white mt-1">{report.reason}</p>
        </div>

        {report.resolution_notes && (
          <div className="mt-4 p-4 bg-green-500/5 rounded-lg border border-green-500/20">
            <Label className="text-green-500 text-sm">Resolution</Label>
            <p className="text-white mt-1">{report.resolution_notes}</p>
            <p className="text-xs text-[#52525B] mt-2">
              {report.action_taken?.replace('_', ' ')} - Resolved on {new Date(report.resolved_at).toLocaleString()}
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reported User */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-red-500" />
            Reported User
          </h3>
          <div className="flex items-center gap-4 mb-4">
            <img
              src={getPhotoUrl(reported_user?.public_photos?.[0]) || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100'}
              alt=""
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <p className="text-white font-medium">{reported_user?.display_name || reported_user?.username}</p>
              <p className="text-[#A1A1AA] text-sm">@{reported_user?.username}</p>
              <p className="text-[#52525B] text-xs">{reported_user?.email}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate(`/admin/users/${reported_user?.id}`)}
          >
            View Full Profile
          </Button>

          {previous_reports?.length > 0 && (
            <div className="mt-4 p-3 bg-red-500/5 rounded-lg border border-red-500/20">
              <p className="text-red-500 text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {previous_reports.length} previous report(s) against this user
              </p>
            </div>
          )}
        </div>

        {/* Reporter */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-[#08D9D6]" />
            Reporter
          </h3>
          <div className="flex items-center gap-4 mb-4">
            <img
              src={getPhotoUrl(reporter?.public_photos?.[0]) || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100'}
              alt=""
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <p className="text-white font-medium">{reporter?.display_name || reporter?.username}</p>
              <p className="text-[#A1A1AA] text-sm">@{reporter?.username}</p>
              <p className="text-[#52525B] text-xs">{reporter?.email}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate(`/admin/users/${reporter?.id}`)}
          >
            View Full Profile
          </Button>
        </div>
      </div>

      {/* Message History */}
      {messages?.length > 0 && (
        <div className="bg-[#111] border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Message History ({messages.length} messages)
          </h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {messages.map((msg) => (
              <div 
                key={msg.id}
                className={`p-3 rounded-lg ${
                  msg.sender_id === reported_user?.id 
                    ? 'bg-red-500/5 border-l-2 border-red-500' 
                    : 'bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-[#A1A1AA]">
                    {msg.sender_id === reported_user?.id ? 'Reported User' : 'Reporter'}
                  </span>
                  <span className="text-xs text-[#52525B]">
                    {new Date(msg.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-white">{msg.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Modal */}
      <Dialog open={!!actionModal} onOpenChange={() => setActionModal(null)}>
        <DialogContent className="bg-[#111] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>
              {actionModal === 'resolve' ? 'Resolve Report' :
               actionModal === 'escalate' ? 'Escalate Report' :
               'Dismiss Report'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {actionModal === 'resolve' && (
              <div className="space-y-2">
                <Label>Action to Take</Label>
                <Select value={selectedAction} onValueChange={setSelectedAction}>
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10">
                    <SelectItem value="no_action">No Action (Warning to reporter)</SelectItem>
                    <SelectItem value="warning_issued">Issue Warning to User</SelectItem>
                    <SelectItem value="content_removed">Remove Content</SelectItem>
                    <SelectItem value="user_suspended">Suspend User</SelectItem>
                    <SelectItem value="user_banned">Ban User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>
                {actionModal === 'resolve' ? 'Resolution Notes' :
                 actionModal === 'escalate' ? 'Escalation Notes' :
                 'Dismissal Reason'}
              </Label>
              <Textarea
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                placeholder="Provide detailed notes..."
                className="bg-white/5 border-white/10 min-h-[120px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionModal(null)}>Cancel</Button>
            <Button 
              onClick={
                actionModal === 'resolve' ? handleResolve :
                actionModal === 'escalate' ? handleEscalate :
                handleDismiss
              }
              disabled={processing}
              className={
                actionModal === 'dismiss' ? 'bg-gray-600 hover:bg-gray-700' :
                actionModal === 'escalate' ? 'bg-orange-600 hover:bg-orange-700' :
                'bg-[#FF2E63] hover:bg-[#E01E50]'
              }
            >
              {processing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReportDetail;
